import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import { lazy, Suspense } from "react";

import { AuthProvider } from "./context/AuthContext";

/* Home stays eagerly imported — it's the landing page, the LCP-critical
   route, and the only page guaranteed-loaded for first-paint optimisation.
   Every other public page is route-split via React.lazy() so the main bundle
   contains just the homepage + shared layout + i18n. This drops the public
   bundle weight by ~30-40 KiB and keeps long tasks low on desktop PSI. */
import Home from "./pages/Home";
const Destinations = lazy(() => import("./pages/Destinations"));
const DestinationOrStoryRoute = lazy(() => import("./components/site/DestinationOrStoryRoute"));
const Experiences = lazy(() => import("./pages/Experiences"));
const ConnoisseursAtelier = lazy(() => import("./pages/ConnoisseursAtelier"));
const ConciergeCity = lazy(() => import("./pages/ConciergeCity"));
const UltraLux = lazy(() => import("./pages/UltraLux"));
const MonthGuide = lazy(() => import("./pages/MonthGuide"));
const PayLink = lazy(() => import("./pages/PayLink"));
const PackagesLanding = lazy(() => import("./pages/PackagesLanding"));
const ConnoisseursResidences = lazy(() => import("./pages/ConnoisseursResidences"));
const ExperienceDetail = lazy(() => import("./pages/ExperienceDetail"));
const About = lazy(() => import("./pages/About"));
const Impact = lazy(() => import("./pages/Impact"));
const Sustainability = lazy(() => import("./pages/Sustainability"));
const Contact = lazy(() => import("./pages/Contact"));
const Retreats = lazy(() => import("./pages/Retreats"));
const Corporate = lazy(() => import("./pages/Corporate"));
const ToursIndex = lazy(() => import("./pages/ToursIndex"));
const PremiumTourLanding = lazy(() => import("./pages/PremiumTourLanding"));
const RuralTourism = lazy(() => import("./pages/RuralTourism"));
const FlipbookGuide = lazy(() => import("./pages/FlipbookGuide"));
const CertificatePage = lazy(() => import("./pages/CertificatePage"));
const AdminCertificates = lazy(() => import("./pages/admin/AdminCertificates"));
const Loyalty = lazy(() => import("./pages/Loyalty"));
const Honeymoons = lazy(() => import("./pages/Honeymoons"));
const Journal = lazy(() => import("./pages/Journal"));
const JournalPost = lazy(() => import("./pages/JournalPost"));
// Legal exports three named pages from one file; webpack will dedupe the
// chunk so all three lazy imports share the same network fetch.
const CancellationRefund = lazy(() => import("./pages/Legal").then((m) => ({ default: m.CancellationRefund })));
const Terms = lazy(() => import("./pages/Legal").then((m) => ({ default: m.Terms })));
const Privacy = lazy(() => import("./pages/Legal").then((m) => ({ default: m.Privacy })));
const CustomerAgreement = lazy(() => import("./pages/Legal").then((m) => ({ default: m.CustomerAgreement })));
const PartnerTerms = lazy(() => import("./pages/Legal").then((m) => ({ default: m.PartnerTerms })));

/* ---------------------------------------------------------------------------
   Admin + Partner routes are code-split via React.lazy() so the public bundle
   doesn't ship the rich-text editor (tiptap/prosemirror, ≈54 KiB of unused
   JS that PSI flagged as the root cause of 19 long tasks on desktop).
   Each admin page becomes its own webpack chunk loaded only when an admin
   actually navigates to /admin/*. Public visitors never download them.
--------------------------------------------------------------------------- */
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminEnquiries = lazy(() => import("./pages/admin/AdminEnquiries"));
const AdminAnalytics = lazy(() => import("./pages/admin/AdminAnalytics"));
const AdminVisitors = lazy(() => import("./pages/admin/AdminVisitors"));
const AdminDestinations = lazy(() => import("./pages/admin/AdminDestinations"));
const AdminExperiences = lazy(() => import("./pages/admin/AdminExperiences"));
const AdminStories = lazy(() => import("./pages/admin/AdminStories"));
const AdminOgRebuild = lazy(() => import("./pages/admin/AdminOgRebuild"));
const AdminTestimonials = lazy(() => import("./pages/admin/AdminTestimonials"));
const AdminNewsletter = lazy(() => import("./pages/admin/AdminNewsletter"));
const AdminZohoCampaigns = lazy(() => import("./pages/admin/AdminZohoCampaigns"));
const AdminPinterest = lazy(() => import("./pages/admin/AdminPinterest"));
const AdminGbpLinks = lazy(() => import("./pages/admin/AdminGbpLinks"));
const AdminPayments = lazy(() => import("./pages/admin/AdminPayments"));
const AdminBroadcasts = lazy(() => import("./pages/admin/AdminBroadcasts"));
const AdminPartners = lazy(() => import("./pages/admin/AdminPartners"));
const AdminBacklinks = lazy(() => import("./pages/admin/AdminBacklinks"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminSecurity = lazy(() => import("./pages/admin/AdminSecurity"));
const AdminLoyalty = lazy(() => import("./pages/admin/AdminLoyalty"));
const AdminCustomers = lazy(() => import("./pages/admin/AdminCustomers"));
const AdminJournal = lazy(() => import("./pages/admin/AdminJournal"));
const PartnerLogin = lazy(() => import("./pages/partner/PartnerLogin"));
const PartnerDashboard = lazy(() => import("./pages/partner/PartnerDashboard"));
const CustomerLogin = lazy(() => import("./pages/CustomerLogin"));
const CustomerAccount = lazy(() => import("./pages/CustomerAccount"));
const ShortLinkRedirect = lazy(() => import("./pages/ShortLinkRedirect"));
import ProtectedRoute from "./pages/admin/ProtectedRoute";
import PWAInstallPrompt from "./components/site/PWAInstallPrompt";
import usePageviewTracking from "./hooks/usePageviewTracking";

function RouteTracker() {
    usePageviewTracking();
    return null;
}

function App() {
    return (
        <HelmetProvider>
            <div className="App">
                <BrowserRouter>
                    <AuthProvider>
                        <RouteTracker />
                        <Toaster
                            position="bottom-center"
                            toastOptions={{
                                style: { fontFamily: "Lato, sans-serif", borderRadius: 0 },
                            }}
                        />
                        <Suspense fallback={
                            <div className="min-h-screen bg-background pt-24 px-6 sm:px-10 max-w-5xl mx-auto" aria-busy="true" data-testid="route-skeleton">
                                <div className="animate-pulse space-y-6">
                                    <div className="h-4 w-32 bg-secondary rounded" />
                                    <div className="h-10 w-3/4 bg-secondary rounded" />
                                    <div className="h-64 w-full bg-secondary rounded" />
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                        {[0, 1, 2].map((i) => <div key={i} className="h-36 bg-secondary rounded" />)}
                                    </div>
                                </div>
                            </div>
                        }>
                            <Routes>
                            <Route path="/" element={<Home />} />
                            <Route path="/destinations" element={<Destinations />} />
                            <Route path="/destinations/:id" element={<DestinationOrStoryRoute />} />
                            <Route path="/experiences" element={<Experiences />} />
                            <Route path="/connoisseurs-atelier" element={<ConnoisseursAtelier />} />
                            <Route path="/ultra-lux-collection" element={<UltraLux />} />
                            <Route path="/where-to-go/:month" element={<MonthGuide />} />
                            <Route path="/pay/:token" element={<PayLink />} />
                            <Route path="/packages/:slug" element={<PackagesLanding />} />
                            <Route path="/connoisseurs-residences" element={<ConnoisseursResidences />} />
                            <Route path="/luxury-travel-concierge-mumbai" element={<ConciergeCity city="mumbai" />} />
                            <Route path="/luxury-travel-concierge-thane" element={<ConciergeCity city="thane" />} />
                            <Route path="/luxury-travel-concierge-jaipur" element={<ConciergeCity city="jaipur" />} />
                            <Route path="/luxury-travel-concierge-visakhapatnam" element={<ConciergeCity city="visakhapatnam" />} />
                            <Route path="/experiences/:id" element={<ExperienceDetail />} />
                            <Route path="/retreats" element={<Retreats />} />
                            <Route path="/corporate" element={<Corporate />} />
                            <Route path="/tours" element={<ToursIndex />} />
                            <Route path="/guide" element={<FlipbookGuide />} />
                            <Route path="/certificate/:id" element={<CertificatePage />} />
                            <Route path="/tours/rural-tourism" element={<RuralTourism />} />
                            <Route path="/tours/:slug" element={<PremiumTourLanding />} />
                            <Route path="/loyalty" element={<Loyalty />} />
                            <Route path="/honeymoons" element={<Honeymoons />} />
                            <Route path="/journal" element={<Journal />} />
                            <Route path="/journal/:slug" element={<JournalPost />} />
                            <Route path="/legal/cancellation-refund" element={<CancellationRefund />} />
                            <Route path="/legal/terms" element={<Terms />} />
                            <Route path="/legal/privacy" element={<Privacy />} />
                            <Route path="/legal/customer-agreement" element={<CustomerAgreement />} />
                            <Route path="/legal/partner-terms" element={<PartnerTerms />} />
                            <Route path="/about" element={<About />} />
                            <Route path="/impact" element={<Impact />} />
                            <Route path="/sustainability" element={<Sustainability />} />
                            <Route path="/contact" element={<Contact />} />
                            <Route path="/login" element={<CustomerLogin />} />
                            <Route path="/account" element={<CustomerAccount />} />
                            <Route path="/p/:slug" element={<ShortLinkRedirect />} />

                            <Route path="/admin/login" element={<AdminLogin />} />
                            <Route
                                path="/admin/loyalty"
                                element={
                                    <ProtectedRoute>
                                        <AdminLoyalty />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/admin/certificates"
                                element={
                                    <ProtectedRoute>
                                        <AdminCertificates />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/admin/customers"
                                element={
                                    <ProtectedRoute>
                                        <AdminCustomers />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/admin"
                                element={
                                    <ProtectedRoute>
                                        <AdminEnquiries />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/admin/analytics"
                                element={
                                    <ProtectedRoute>
                                        <AdminAnalytics />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/admin/visitors"
                                element={
                                    <ProtectedRoute>
                                        <AdminVisitors />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/admin/destinations"
                                element={
                                    <ProtectedRoute>
                                        <AdminDestinations />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/admin/experiences"
                                element={
                                    <ProtectedRoute>
                                        <AdminExperiences />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/admin/stories"
                                element={
                                    <ProtectedRoute>
                                        <AdminStories />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/admin/security"
                                element={
                                    <ProtectedRoute>
                                        <AdminSecurity />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/admin/og-rebuild"
                                element={
                                    <ProtectedRoute>
                                        <AdminOgRebuild />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/admin/testimonials"
                                element={
                                    <ProtectedRoute>
                                        <AdminTestimonials />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/admin/newsletter"
                                element={
                                    <ProtectedRoute>
                                        <AdminNewsletter />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/admin/zoho-campaigns"
                                element={
                                    <ProtectedRoute role="admin">
                                        <AdminZohoCampaigns />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/admin/pinterest"
                                element={
                                    <ProtectedRoute role="admin">
                                        <AdminPinterest />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/admin/gbp"
                                element={
                                    <ProtectedRoute role="admin">
                                        <AdminGbpLinks />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/admin/payments"
                                element={
                                    <ProtectedRoute role="admin">
                                        <AdminPayments />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/admin/broadcasts"
                                element={
                                    <ProtectedRoute role="admin">
                                        <AdminBroadcasts />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/admin/partners"
                                element={
                                    <ProtectedRoute role="admin">
                                        <AdminPartners />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/admin/backlinks"
                                element={
                                    <ProtectedRoute role="admin">
                                        <AdminBacklinks />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/admin/settings"
                                element={
                                    <ProtectedRoute role="admin">
                                        <AdminSettings />
                                    </ProtectedRoute>
                                }
                            />
                            <Route path="/partner/login" element={<PartnerLogin />} />
                            <Route
                                path="/partner"
                                element={
                                    <ProtectedRoute role="partner">
                                        <PartnerDashboard />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/admin/journal"
                                element={
                                    <ProtectedRoute>
                                        <AdminJournal />
                                    </ProtectedRoute>
                                }
                            />
                        </Routes>
                        </Suspense>
                        <PWAInstallPrompt />
                    </AuthProvider>
                </BrowserRouter>
            </div>
        </HelmetProvider>
    );
}

export default App;
